// ============================================================================
// Payment Status Query — production React replica of the Evalueserve Figma file.
// Class-based styling variant: all view-specific styles live in spendiq.css.
// ============================================================================
import React, { useState, useEffect, useRef, useCallback } from "react";
import { GroupMailboxView } from "./payment-status-query/GroupMailboxView";
import { CorpusInsightsAndModelsView } from "./payment-status-query/CorpusInsightsAndModelsView";
import { BusinessValueView } from "./payment-status-query/BusinessValueView";
import { C } from "./payment-status-query/data";
import { Eyebrow, GradButton, GlobalStyles, Ic, usePrefersReducedMotion } from "./payment-status-query/shared";

const CASE = {
  id: "EM-4471",
  subject: "Payment status for invoice INV-2026-4471 (PO 4500098231)",
  meta: "Bosch GmbH - Accounts Receivable  -  accounts@boschgmbh.com  -  Today 07:42",
  body: "Hello team, our invoice INV-2026-4471 for EUR 148,320 against PO 4500098231 was submitted on 12 June. It is now 23 days past the agreed due date and we have had no remittance advice. Could you confirm when payment will be released?",
  tags: [
    { text: "PAYMENT STATUS CHECK - 97%", kind: "purple" },
    { text: "PO 4500098231 - INV-2026-4471", kind: "grey" },
    { text: "C1 - Overdue / no remittance", kind: "grey" },
  ],
};

const SYSTEMS = ["Ariba Network", "BEDW", "S/4HANA", "SPVR", "Webi Reporting"];
const SYSTEMS_SHORT = ["Ariba", "BEDW", "S/4HANA", "SPVR", "Webi"];

const AGENT_DESIGN = [
  "Orchestrator agent plans the lookup sequence from the extracted identifiers",
  "Tool agents call Ariba, BEDW, S/4HANA, SPVR and Webi in parallel where possible",
  "Reasoning layer converts raw system fields into a human-readable hold-up story",
  "Draft reply is generated in the helpdesk's own tone of voice",
];
const GUARDRAILS = [
  { k: "Confidence Threshold", v: "Auto-reply only above 85%" },
  { k: "Exclusions", v: "No bank detail changes, no payment release" },
  { k: "Data Scope", v: "Read-only access to all five systems" },
  { k: "Escalation", v: "Anything unresolved after 2 attempts goes to AP" },
];

const HOLD_UP = "The invoice is approved on the network and posted in the ERP, but it is parked with payment block R because the goods receipt is incomplete: only line 1 has a GR. 400 of the 1,000 units on line 2 were never receipted, so the payment run skipped the document on 01 July. Vendor master, banking and sanctions are all clear - receipting is the single blocker.";
const NEXT_STEPS = [
  "1. Ask requisitioner R. Mehta to post GR for the outstanding 400 units on line 2",
  "2. If goods were never delivered, short-close line 2 and ask the supplier for a credit note",
  "3. Once GR posts, release payment block R on document 5105538812",
];
const EXPEDITE = [
  "1. Flag document to the 09 July off-cycle payment run once the block clears",
  "2. Notify treasury - supplier is on Net 45 with a late-payment interest clause",
  "3. Send the supplier a dated commitment instead of a generic holding reply",
];
const REPLY = {
  greet: "Dear Bosch AR team,",
  body: "Thank you for the follow-up on INV-2026-4471 (PO 4500098231). The invoice is fully approved, but payment is currently blocked because the goods receipt for 400 of the 1,000 units on line 2 has not been posted. We have asked the requisitioning plant to complete the receipt today. Once posted, the invoice will be picked up in the off-cycle run on 09 July and remittance advice will follow automatically.",
  sign: "Kind regards, Accounts Payable Helpdesk",
  foot: "Resolved in 41 seconds  -  5 systems  -  0 manual lookups",
};

const TABS = [
  { key: "mailbox", label: "Group Mailbox" },
  { key: "corpus", label: "Corpus Insights & Model" },
  { key: "value", label: "Business Value" },
];

function PageHeader() {
  return (
    <div className="psq-page-header">
      <span className="psq-page-eyebrow">Invoice &amp; Payments</span>
      <h1 className="psq-page-title">Payment Status Query</h1>
    </div>
  );
}

function ViewTabs({ active, onChange, rightSlot }: any) {
  return (
    <div className="psq-view-tabs" role="tablist" aria-label="Views">
      {TABS.map((t) => {
        const on = active === t.key;
        return (
          <button
            key={t.key}
            role="tab"
            aria-selected={on}
            onClick={() => onChange(t.key)}
            className={on ? "psq-view-tab is-active" : "psq-view-tab"}
          >
            {t.label}
          </button>
        );
      })}
      {rightSlot}
    </div>
  );
}

function CaseTag({ kind, text }: any) {
  if (kind === "purple") {
    return (
      <span className="psq-case-tag psq-case-tag--purple">
        <Ic.sparkles width={12} height={12} className="psq-case-tag-icon" />
        {text}
      </span>
    );
  }
  return <span className="psq-case-tag psq-case-tag--grey">{text}</span>;
}

function CaseContextCard({ statusPill }: any) {
  return (
    <div className="psq-card psq-card--case">
      <div className="psq-row psq-row--between psq-row--wrap psq-gap-16">
        <div className="psq-col psq-gap-4 psq-minw-0">
          <Eyebrow>Case {CASE.id}</Eyebrow>
          <h2 className="psq-case-title">{CASE.subject}</h2>
          <span className="psq-case-meta">{CASE.meta}</span>
        </div>
        {statusPill}
      </div>
      <div className="psq-case-body-wrap">
        <p className="psq-case-body">{CASE.body}</p>
      </div>
      <div className="psq-tags-row">
        {CASE.tags.map((t: any, i: number) => (
          <CaseTag key={i} kind={t.kind} text={t.text} />
        ))}
      </div>
    </div>
  );
}

function GuidesPanel() {
  return (
    <div className="psq-col psq-gap-20">
      <div className="psq-card psq-card--guide">
        <Eyebrow>Agent Design</Eyebrow>
        <div className="psq-col psq-gap-14">
          {AGENT_DESIGN.map((s, i) => (
            <div key={i} className="psq-row psq-gap-12 psq-items-start">
              <span className="psq-step-dot">{i + 1}</span>
              <span className="psq-guide-copy">{s}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="psq-card psq-card--guide">
        <Eyebrow>Guardrails</Eyebrow>
        <div className="psq-col psq-gap-14">
          {GUARDRAILS.map((g, i) => (
            <div key={i} className="psq-col psq-gap-4">
              <span className="psq-guard-key">{g.k}</span>
              <span className="psq-guard-val">{g.v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CaseReview({ onRun, showGuides }: any) {
  const reduce = usePrefersReducedMotion();
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(-1);

  const run = () => {
    if (reduce) {
      onRun();
      return;
    }
    setRunning(true);
    let i = 0;
    setProgress(0);
    const tick = () => {
      i += 1;
      setProgress(i);
      if (i >= SYSTEMS.length) {
        setTimeout(onRun, 350);
        return;
      }
      setTimeout(tick, 260);
    };
    setTimeout(tick, 260);
  };

  const statusOf = (idx: number) => {
    if (!running) return "Queued";
    if (idx < progress) return "Done";
    if (idx === progress) return "Querying";
    return "Queued";
  };

  return (
    <div className={showGuides ? "psq-case-grid psq-case-grid--with-guides" : "psq-case-grid"}>
      <div className="psq-col psq-gap-20">
        <div className="psq-card psq-card--case">
          <div className="psq-row psq-row--between psq-row--wrap psq-gap-16">
            <div className="psq-col psq-gap-4 psq-minw-0">
              <Eyebrow>Case {CASE.id}</Eyebrow>
              <h2 className="psq-case-title">{CASE.subject}</h2>
              <span className="psq-case-meta">{CASE.meta}</span>
            </div>
            <GradButton onClick={run} disabled={running}>
              <Ic.sparkles width={14} height={14} className={running ? "np-spin" : ""} />
              {running ? "Running..." : "Run resolution agent"}
            </GradButton>
          </div>
          <div className="psq-case-body-wrap">
            <p className="psq-case-body psq-case-body--lg">{CASE.body}</p>
          </div>
          <div className="psq-tags-row">
            {CASE.tags.map((t: any, i: number) => (
              <CaseTag key={i} kind={t.kind} text={t.text} />
            ))}
          </div>
        </div>

        <div className="psq-card psq-card--orchestrator">
          <Eyebrow>Orchestrator - System Fan-Out</Eyebrow>
          <div className="psq-system-progress-row">
            {SYSTEMS.map((s, i) => {
              const st = statusOf(i);
              const activeDot = st !== "Queued";
              return (
                <div key={i} className="psq-system-progress-item">
                  <div className={activeDot ? "psq-system-dot is-active" : "psq-system-dot"}>{st === "Done" ? "✓" : i + 1}</div>
                  <span className="psq-system-name">{s}</span>
                </div>
              );
            })}
          </div>

          <div className="psq-sys-cards">
            {SYSTEMS.map((s, i) => {
              const st = statusOf(i);
              return (
                <div key={i} className="psq-sys-card">
                  <div className="psq-row psq-row--between psq-gap-6">
                    <span className="psq-sys-card-title">{s}</span>
                    <span className={st === "Done" ? "psq-sys-pill is-done" : "psq-sys-pill"}>{st}</span>
                  </div>
                  <span className="psq-sys-card-sub">{st}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showGuides && <GuidesPanel />}
    </div>
  );
}

function Resolution({ onSend, onEscalate, showGuides }: any) {
  return (
    <div className={showGuides ? "psq-case-grid psq-case-grid--with-guides" : "psq-case-grid"}>
      <div className="psq-col psq-gap-20">
        <CaseContextCard statusPill={<span className="psq-status-pill">Resolution complete</span>} />

        <div className="psq-ribbon">
          <div className="psq-ribbon-check">✓</div>
          <div className="psq-col psq-gap-3 psq-flex-1 psq-minw-0">
            <span className="psq-ribbon-title">Orchestrator - system fan-out complete</span>
            <span className="psq-ribbon-sub">5 systems queried  -  evidence reconciled  -  response ready</span>
          </div>
          <div className="psq-ribbon-tags">
            {SYSTEMS_SHORT.map((s, i) => (
              <span key={i} className="psq-ribbon-tag">✓ {s}</span>
            ))}
          </div>
        </div>

        <div className="psq-hold-card">
          <div className="psq-row psq-row--between psq-row--wrap psq-gap-12">
            <div className="psq-row psq-items-center psq-gap-8">
              <span className="psq-ai-chip">AI</span>
              <span className="psq-hold-title">Where the payment is held up</span>
            </div>
            <span className="psq-confidence-chip">94% CONFIDENCE</span>
          </div>
          <p className="psq-hold-copy">{HOLD_UP}</p>
        </div>

        <div className="psq-2col">
          <div className="psq-card psq-card--sub">
            <Eyebrow>Next steps to fix the issue</Eyebrow>
            <div className="psq-col psq-gap-8">
              {NEXT_STEPS.map((s, i) => (
                <span key={i} className="psq-small-copy">{s}</span>
              ))}
            </div>
          </div>
          <div className="psq-card psq-card--sub">
            <Eyebrow>Expedite path (23 days past due)</Eyebrow>
            <div className="psq-col psq-gap-8">
              {EXPEDITE.map((s, i) => (
                <span key={i} className="psq-small-copy">{s}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="psq-card psq-card--reply">
          <div className="psq-row psq-items-center psq-gap-8">
            <Ic.mail width={14} height={14} className="psq-mail-icon" />
            <span className="psq-reply-label">AI drafted reply - accounts@boschgmbh.com</span>
          </div>
          <div className="psq-reply-box">
            <span className="psq-reply-line">{REPLY.greet}</span>
            <span className="psq-reply-line psq-reply-line--body">{REPLY.body}</span>
            <span className="psq-reply-line">{REPLY.sign}</span>
          </div>
          <div className="psq-row psq-row--between psq-row--wrap psq-gap-12">
            <span className="psq-reply-foot">{REPLY.foot}</span>
            <div className="psq-row psq-gap-12">
              <GradButton onClick={onSend}>Send reply &amp; close case</GradButton>
              <button onClick={onEscalate} className="psq-btn-secondary">Escalate to human</button>
            </div>
          </div>
        </div>
      </div>

      {showGuides && <GuidesPanel />}
    </div>
  );
}

function Toast({ toast, onDismiss }: any) {
  if (!toast) return null;
  return (
    <div className="np-toast psq-toast" role="status" aria-live="polite">
      <div className="psq-toast-icon-wrap">
        <svg viewBox="0 0 24 24" width={16} height={16} fill="none">
          <path d="m5 12 4.5 4.5L19 7" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className="psq-toast-body">
        <span className="psq-toast-title">{toast.title}</span>
        <span className="psq-toast-sub">{toast.sub}</span>
      </div>
      <button onClick={onDismiss} aria-label="Dismiss" className="psq-toast-close">
        <Ic.x width={14} height={14} />
      </button>
    </div>
  );
}

function GuidesPill({ on, onClick }: any) {
  return (
    <button onClick={onClick} aria-pressed={on} className="psq-guides-pill">
      <Ic.panel width={14} height={14} />
      {on ? "Hide guides" : "Show guides"}
    </button>
  );
}

export function PaymentStatusQuery() {
  const [tab, setTab] = useState("mailbox");
  const [triaged, setTriaged] = useState(false);
  const [openCase, setOpenCase] = useState<any>(null);
  const [caseStage, setCaseStage] = useState("review");
  const [closedIds, setClosedIds] = useState<string[]>([]);
  const [toast, setToast] = useState<any>(null);
  const [showGuides, setShowGuides] = useState(true);
  const toastTimer = useRef<any>(null);

  const fireToast = useCallback((t: any) => {
    setToast(t);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5200);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const changeTab = (k: string) => {
    setTab(k);
    setOpenCase(null);
  };

  const openCaseFn = (id: string) => {
    setOpenCase(id);
    setCaseStage("review");
  };

  const resolveCase = () => setCaseStage("resolved");

  const sendAndClose = () => {
    fireToast({
      title: "Reply sent, case closed.",
      sub: "Reply sent - case closed - GR chase task created for R. Mehta",
    });
    setClosedIds((c) => (c.includes("EM-4471") ? c : [...c, "EM-4471"]));
    setOpenCase(null);
  };

  const escalate = () => {
    fireToast({
      title: "Escalated to human",
      sub: "Case EM-4471 routed to an AP specialist with the extracted context attached.",
    });
    setOpenCase(null);
  };

  const caseOpen = tab === "mailbox" && openCase;

  return (
    <div className="psq-root">
      <GlobalStyles />
      <div className="psq-shell">
        <PageHeader />
        <ViewTabs
          active={tab}
          onChange={changeTab}
          rightSlot={
            caseOpen ? <GuidesPill on={showGuides} onClick={() => setShowGuides((v) => !v)} /> : null
          }
        />

        {tab === "mailbox" && !caseOpen && (
          <GroupMailboxView triaged={triaged} onTriage={setTriaged} onOpenCase={openCaseFn} closedIds={closedIds} />
        )}
        {tab === "mailbox" && caseOpen && caseStage === "review" && (
          <CaseReview onRun={resolveCase} showGuides={showGuides} />
        )}
        {tab === "mailbox" && caseOpen && caseStage === "resolved" && (
          <Resolution onSend={sendAndClose} onEscalate={escalate} showGuides={showGuides} />
        )}
        {tab === "corpus" && <CorpusInsightsAndModelsView />}
        {tab === "value" && <BusinessValueView />}
      </div>
      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
